import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { sendAnalytics } from '../../../analytics/functions';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import Icon from '../../../base/icons/components/Icon';
import { IconInterpreting } from '../../../base/icons/svg';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { showNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { queueService } from '../../../interpreter-queue/InterpreterQueueService';
import { canRequestP2PInterpreter } from '../../../interpreter-queue/p2pInterpreterEligibility';
import { p2pInterpreterRequestTarget } from '../../../interpreter-queue/p2pInterpreterRequest';
import { getPersistentJson } from '../../../vrs-auth/storage';
let requestSequence = 0;
const useStyles = makeStyles()(theme => ({
    wrapper: { display: 'inline-flex', margin: '0 8px' },
    cta: {
        background: '#15803D',
        border: '1px solid #4ADE80',
        '&:hover': { background: '#166534' },
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    pending: { background: '#166534', border: '1px solid #86EFAC' }
}));
/**
 * The P2P in-call "interpreting" button: centered green affordance, visible
 * only to the call originator, pulling a terp into the LIVE room. Server
 * contract: p2p_request_interpreter → p2p_interpreter_requested ack,
 * p2p_interpreter_joined / p2p_interpreter_unavailable verdicts.
 */
export default function P2PInterpretingButton() {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [pending, setPending] = useState(false);
    const [joined, setJoined] = useState(false);
    const [eligible, setEligible] = useState(canRequestP2PInterpreter);
    const [callId, setCallId] = useState('');
    useEffect(() => {
        setEligible(canRequestP2PInterpreter());
        const call = getPersistentJson('vrs_active_call');
        setCallId(typeof call?.callId === 'string' ? call.callId : '');
    }, []);
    useEffect(() => {
        if (!eligible) {
            return;
        }
        const notify = (title, description) => dispatch(showNotification({
            descriptionKey: undefined,
            description,
            title,
            titleKey: undefined
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        const onJoined = (data) => {
            setPending(false);
            setJoined(true);
            notify(t('toolbar.interpreterJoined', 'Interpreter joined the call'), data?.interpreterName
                || t('toolbar.interpreterJoinedBody', 'They are signing in your room now.'));
        };
        const onUnavailable = () => {
            setPending(false);
            notify(t('toolbar.requestFailed', 'Request failed'), t('toolbar.noInterpreterAvailable', 'No interpreters are available right now. Try again shortly.'));
        };
        const onError = (data) => {
            if (data?.operation !== 'p2p_request_interpreter') {
                return;
            }
            setPending(false);
            if (data.code === 'P2P_INTERPRETER_PRESENT') {
                setJoined(true);
            }
            else if (data.code === 'ALREADY_REQUESTED') {
                setPending(true);
            }
            else {
                notify(t('toolbar.requestFailed', 'Request failed'), data.message);
            }
        };
        queueService.on('p2pInterpreterJoined', onJoined);
        queueService.on('p2pInterpreterUnavailable', onUnavailable);
        queueService.on('error', onError);
        return () => {
            queueService.off('p2pInterpreterJoined', onJoined);
            queueService.off('p2pInterpreterUnavailable', onUnavailable);
            queueService.off('error', onError);
        };
    }, [eligible, dispatch, t]);
    const press = useCallback(() => {
        if (pending || joined) {
            return;
        }
        const call = getPersistentJson('vrs_active_call');
        if (!call?.callId || !call.roomName) {
            return;
        }
        setPending(true);
        sendAnalytics(createToolbarEvent('p2p.interpreter.requested'));
        queueService.sendP2PRequestInterpreter(call.callId, call.roomName, `p2p-btn-${Date.now()}-${++requestSequence}`, p2pInterpreterRequestTarget());
    }, [pending, joined]);
    if (!eligible || joined || !callId) {
        return null;
    }
    return (React.createElement("div", { className: classes.wrapper },
        React.createElement(Button, { accessibilityLabel: t('toolbar.requestInterpreterInCall', 'Call an interpreter into this call'), className: pending ? classes.pending : classes.cta, icon: React.createElement(Icon, { src: IconInterpreting, size: 20 }), label: pending
                ? t('toolbar.requestingInterpreter', 'Requesting…')
                : t('toolbar.interpreter', 'Interpreter'), onClick: press, type: BUTTON_TYPES.PRIMARY })));
}
